import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../state/auth.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { GoogleButton, GOOGLE_CLIENT_ID } from '../components/GoogleButton.js';
import { useT } from '../state/i18n.js';
import { trackSiteEvent } from '../lib/site-tracker.js';
import {
  isWorkspaceHost,
  isCustomTenantHost,
  isAllowedReturnUrl,
  buildRootLoginUrl,
} from '../lib/auth-host.js';

type Mode = 'login' | 'signup' | 'reset-request' | 'reset-confirm' | 'verify';

/** 로그인 뒤 리디렉션은 같은 사이트의 절대 경로만 허용한다(open redirect 차단). */
function safeReturnTo(value: string | null): string {
  return value?.startsWith('/') && !value.startsWith('//') ? value : '/app';
}

export function LoginPage(): JSX.Element {
  const auth = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resetToken = params.get('resetToken') ?? '';
  const verifyToken = params.get('verifyToken') ?? '';
  const returnTo = safeReturnTo(params.get('returnTo'));
  // 루트 로그인 완료 후 돌려보낼 절대 URL(서브도메인). 화이트리스트 통과분만 사용.
  const returnUrl = params.get('return');
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const onWorkspaceHost = isWorkspaceHost(host);
  const returning = useRef(false);

  const [mode, setMode] = useState<Mode>(
    resetToken ? 'reset-confirm' : verifyToken ? 'verify' : 'login',
  );

  // 로그인 성공 후 이동 — 루트에서 유효한 return(서브도메인) 이 있으면 그리로, 아니면 내부 경로.
  async function finishLogin(): Promise<void> {
    if (returnUrl && isAllowedReturnUrl(returnUrl)) {
      const target = new URL(returnUrl);
      if (isCustomTenantHost(target.hostname)) {
        const handoff = await api.issueAuthHandoff(target.toString());
        target.searchParams.set('handoff', handoff.code);
      }
      window.location.replace(target.toString());
      return;
    }
    navigate(returnTo);
  }

  // 서브도메인에서는 Google origin 등록이 불가 → 로그인을 루트로 집중.
  // ★즉시 리디렉트한다 — /api/me(콜드스타트 수 초) 를 기다리지 않는다(그게 "이동 중" 8초의 원인).
  // 이미 로그인한 유저는 루트 /login 이 아래 이펙트로 곧장 return 으로 되돌린다.
  useEffect(() => {
    if (!onWorkspaceHost) return;
    if (mode !== 'login' && mode !== 'signup') return;
    const returnPath = isCustomTenantHost(window.location.hostname) ? '/lake' : '/documents';
    window.location.replace(buildRootLoginUrl(`${window.location.origin}${returnPath}`));
  }, [onWorkspaceHost, mode]);

  // 루트에서: 이미 로그인돼 있고 유효한 return 이 있으면(=서브도메인에서 튕겨온 인증 유저) 곧장 되돌린다.
  useEffect(() => {
    if (onWorkspaceHost || auth.loading || returning.current) return;
    if (auth.user && returnUrl && isAllowedReturnUrl(returnUrl)) {
      returning.current = true;
      void finishLogin().catch((err) => {
        returning.current = false;
        setStatus({
          tone: 'err',
          text: err instanceof Error ? err.message : 'Could not open workspace.',
        });
      });
    }
  }, [onWorkspaceHost, auth.loading, auth.user, returnUrl]);

  useDocumentMeta({
    title:
      mode === 'signup'
        ? t({ ko: 'CloudBTL 계정 만들기', en: 'Create your CloudBTL account' })
        : mode === 'reset-request' || mode === 'reset-confirm'
          ? t({ ko: '비밀번호 재설정', en: 'Reset password' })
          : mode === 'verify'
            ? t({ ko: '이메일 인증', en: 'Verify your email' })
            : t({ ko: '로그인', en: 'Sign in' }),
    description: t({
      ko: 'CloudBTL 워크스페이스에 접속하세요.',
      en: 'Access your CloudBTL workspace.',
    }),
    noindex: resetToken !== '' || verifyToken !== '',
    canonical: '/login',
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode !== 'verify') return;
    if (!verifyToken) return;
    setBusy(true);
    api
      .confirmVerification(verifyToken)
      .then(() =>
        setStatus({
          tone: 'ok',
          text: t({
            ko: '이메일이 인증되었습니다. 로그인할 수 있어요.',
            en: 'Email verified. You can sign in.',
          }),
        }),
      )
      .catch((err: ApiError) => setStatus({ tone: 'err', text: err.message }))
      .finally(() => setBusy(false));
  }, [mode, verifyToken, t]);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      if (mode === 'login') {
        await auth.login(email, password);
        trackSiteEvent('account_login', { method: 'password' });
        await finishLogin();
      } else if (mode === 'signup') {
        await auth.signup(email, password, name);
        trackSiteEvent('account_signup', { method: 'password' });
        finishLogin();
      } else if (mode === 'reset-request') {
        const result = await api.requestPasswordReset(email);
        setStatus({ tone: 'ok', text: result.message });
      } else if (mode === 'reset-confirm') {
        await api.confirmPasswordReset(resetToken, password);
        await auth.refresh();
        navigate('/app');
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t({ ko: '문제가 발생했습니다.', en: 'Something went wrong.' });
      setStatus({ tone: 'err', text: message });
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle(credential: string): Promise<void> {
    setBusy(true);
    setStatus(null);
    try {
      await auth.loginWithGoogle(credential);
      trackSiteEvent(mode === 'signup' ? 'account_signup' : 'account_login', {
        method: 'google',
      });
      await finishLogin();
    } catch (err) {
      setStatus({
        tone: 'err',
        text:
          err instanceof Error
            ? err.message
            : t({ ko: 'Google 로그인 실패.', en: 'Google sign-in failed.' }),
      });
    } finally {
      setBusy(false);
    }
  }

  const showGoogle = (mode === 'login' || mode === 'signup') && Boolean(GOOGLE_CLIENT_ID);

  // 서브도메인 로그인/가입은 루트로 리디렉트되므로 폼·GoogleButton 을 아예 렌더하지 않는다
  // (GIS 가 미등록 origin 에서 초기화되며 origin_mismatch 를 던지는 걸 막는다).
  if (onWorkspaceHost && (mode === 'login' || mode === 'signup')) {
    return (
      <Shell>
        <section className="auth-panel">
          <p className="status ok">
            {t({ ko: '로그인 페이지로 이동 중…', en: 'Redirecting to sign in…' })}
          </p>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="auth-panel">
        <h1>
          {mode === 'login'
            ? t({ ko: '로그인', en: 'Sign in' })
            : mode === 'signup'
              ? t({ ko: 'CloudBTL 계정 만들기', en: 'Create your CloudBTL account' })
              : mode === 'reset-request'
                ? t({ ko: '비밀번호 재설정', en: 'Reset password' })
                : mode === 'reset-confirm'
                  ? t({ ko: '새 비밀번호 설정', en: 'Choose a new password' })
                  : t({ ko: '이메일 인증', en: 'Verify your email' })}
        </h1>

        {mode === 'verify' ? (
          <p className={status?.tone === 'err' ? 'status err' : 'status ok'}>
            {busy
              ? t({ ko: '인증 중…', en: 'Verifying…' })
              : (status?.text ?? t({ ko: '인증 중…', en: 'Verifying…' }))}
          </p>
        ) : (
          <form onSubmit={onSubmit}>
            {mode === 'signup' ? (
              <label>
                {t({ ko: '이름', en: 'Name' })}
                <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </label>
            ) : null}
            {mode !== 'reset-confirm' ? (
              <label>
                {t({ ko: '이메일', en: 'Email' })}
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </label>
            ) : null}
            {mode === 'login' || mode === 'signup' || mode === 'reset-confirm' ? (
              <label>
                {t({ ko: '비밀번호', en: 'Password' })}
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
              </label>
            ) : null}
            <button className="cta-primary" type="submit" disabled={busy}>
              {busy
                ? t({ ko: '처리 중…', en: 'Working…' })
                : mode === 'login'
                  ? t({ ko: '로그인', en: 'Sign in' })
                  : mode === 'signup'
                    ? t({ ko: '계정 만들기', en: 'Create account' })
                    : mode === 'reset-request'
                      ? t({ ko: '재설정 링크 보내기', en: 'Send reset link' })
                      : t({ ko: '비밀번호 저장', en: 'Save password' })}
            </button>
            {status ? <p className={`status ${status.tone}`}>{status.text}</p> : null}
          </form>
        )}

        {showGoogle ? (
          <div className="auth-google">
            <div className="auth-divider">
              <span>{t({ ko: '또는', en: 'or' })}</span>
            </div>
            <GoogleButton
              onCredential={onGoogle}
              text={mode === 'signup' ? 'signup_with' : 'signin_with'}
              onError={(text) => setStatus({ tone: 'err', text })}
            />
          </div>
        ) : null}

        {mode === 'login' || mode === 'signup' ? (
          <p className="auth-consent" data-testid="auth-consent">
            {t({ ko: '계속하면 ', en: 'By continuing, you agree to our ' })}
            <Link to="/legal/terms">{t({ ko: '이용약관', en: 'Terms' })}</Link>
            {t({ ko: ' 및 ', en: ' and ' })}
            <Link to="/legal/privacy">{t({ ko: '개인정보 처리방침', en: 'Privacy Policy' })}</Link>
            {t({ ko: '에 동의하게 됩니다.', en: '.' })}
          </p>
        ) : null}

        <div className="auth-switch">
          {mode === 'login' ? (
            <>
              <button className="link" onClick={() => setMode('signup')}>
                {t({ ko: '계정이 없나요? 회원가입', en: 'Need an account? Sign up' })}
              </button>
              <button className="link" onClick={() => setMode('reset-request')}>
                {t({ ko: '비밀번호를 잊으셨나요?', en: 'Forgot password?' })}
              </button>
            </>
          ) : null}
          {mode === 'signup' ? (
            <button className="link" onClick={() => setMode('login')}>
              {t({ ko: '이미 계정이 있나요?', en: 'Already have an account?' })}
            </button>
          ) : null}
          {mode === 'reset-request' ? (
            <button className="link" onClick={() => setMode('login')}>
              {t({ ko: '로그인으로 돌아가기', en: 'Back to sign in' })}
            </button>
          ) : null}
        </div>
      </section>
    </Shell>
  );
}
